import { FROST_BREATH_REST } from '../../models/frost-colossus.ts';
import { clip, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inExpo, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';

/*
 * Coloso de escarcha (models/frost-colossus.ts): a glacier that walks. Everything it does is slow to start and
 * enormous when it lands: it hauls its weight from foot to foot, swings its arms like a gorilla's, its knuckles
 * passing a hand over the ground.
 *
 * Conventions as in human.ts: −X on an arm or leg swings it forward (up, past the horizontal, overhead), +X on a
 * shin bends the knee, −X on a forearm bends the elbow; +X on the trunk or the head leans it forward; +X on the
 * jaw opens it; +Z on the right arm (+X) lifts it out to the side, −Z the left. `breath` is built tiny at the
 * mouth: only the breath clip scales it up (`1 / FROST_BREATH_REST`), every other clip leaves it hidden.
 *
 * Blows: attack (a hammer of the right fist) lands at 0.13 s (it starts raised); slam (both fists into the
 * ground) lands at 0.75 s; the breath blows from 0.75 s to 1.75 s.
 */

type B =
  | 'root' | 'body' | 'chest' | 'head' | 'jaw' | 'breath'
  | 'armL' | 'foreL' | 'handL' | 'armR' | 'foreR' | 'handR'
  | 'legL' | 'shinL' | 'legR' | 'shinR';
type P = KeyPose<B>;

const BLAST = 1 / FROST_BREATH_REST;

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.6 * k, 1 - k, 1 + 0.6 * k];
};

const idle = (): Clip<B> =>
  clip<B>('idle', 4.5, true, (b, t) => {
    const p = phase(t, 4.5) * TAU;
    const br = Math.sin(p * 2);
    b.root.pos(0, -0.012 * (1 + br), 0).rot(0, 0, 0.012 * Math.sin(p));
    b.body.rot(0.01 * br, 0, 0.015 * Math.sin(p));
    // The great chest heaves; the shoulders rise with it.
    b.chest.rot(-0.035 * br, 0.05 * Math.sin(p), 0.02 * Math.sin(p + 0.4)).scale(1 + 0.008 * br);
    b.head.rot(0.04 * br, 0.28 * Math.sin(p + 0.6), 0.05 * Math.sin(p));
    b.jaw.rot(0.06 * (1 + Math.sin(p * 2 + 0.5)));
    b.armL.rot(0.04 * Math.sin(p + 0.3), 0, -0.03 * br);
    b.armR.rot(0.04 * Math.sin(p + 1.3), 0, 0.03 * br);
    b.foreL.rot(-0.04 * Math.sin(p + 0.8));
    b.foreR.rot(-0.04 * Math.sin(p + 1.8));
    b.handL.rot(0.08 * Math.sin(p * 2 + 1));
    b.handR.rot(0.08 * Math.sin(p * 2 + 2.4));
  }, (t) => {
    const br = Math.sin(phase(t, 4.5) * TAU * 2);
    return [1 + 0.008 * br, 1 - 0.006 * br, 1 + 0.008 * br];
  });

const joltAt = (t: number, dur: number, freq: number, damp: number): number => {
  const half = dur / 2;
  return settle((((t - dur / 4) % half) + half) % half, freq, damp);
};

/** The lumber: short heavy strides, the whole mass rolling over each planted foot, the arms swinging like bells. */
const walk = (dur = 2.6): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    const jolt = Math.max(0, joltAt(t, dur, 2.0, 6));
    const A = 0.3;
    b.legL.rot(-A * sn - 0.04, 0, -0.03);
    b.legR.rot(A * sn - 0.04, 0, 0.03);
    b.shinL.rot(0.6 * Math.max(0, cs) ** 1.5 + 0.05);
    b.shinR.rot(0.6 * Math.max(0, -cs) ** 1.5 + 0.05);
    b.root.rot(0.02 * jolt, 0.05 * sn, -0.06 * cs).pos(-0.05 * cs, 0.025 * Math.cos(2 * th) - 0.03 - 0.04 * jolt, 0);
    b.body.rot(0.06 + 0.03 * jolt, 0.08 * sn, 0.03 * cs);
    b.chest.rot(0.05 + 0.03 * Math.cos(2 * th - 0.7), -0.14 * sn, 0.04 * cs);
    b.head.rot(-0.08 - 0.05 * jolt, 0.1 * sn, -0.03 * cs);
    b.jaw.rot(0.05 * jolt);
    const sL = Math.sin(th - 0.4);
    b.armL.rot(0.32 * sL, 0, -0.04);
    b.armR.rot(-0.32 * sL, 0, 0.04);
    b.foreL.rot(-0.2 * Math.max(0, -Math.sin(th - 0.8)) + 0.05 * jolt);
    b.foreR.rot(-0.2 * Math.max(0, Math.sin(th - 0.8)) + 0.05 * jolt);
    b.handL.rot(0.25 * Math.sin(th - 1.1));
    b.handR.rot(-0.25 * Math.sin(th - 1.1));
  }, (t) => {
    const j = Math.max(0, joltAt(t, dur, 2.0, 9));
    return [1 + 0.03 * j, 1 - 0.04 * j, 1 + 0.03 * j];
  });

/** Raised: the right fist up over its shoulder, the trunk reared and turned. */
const RAISED: P = {
  body: [-0.06, -0.12, 0],
  chest: [-0.2, -0.3, 0.05],
  head: [-0.12, 0.25, 0],
  jaw: [0.35, 0, 0],
  armR: [-2.5, 0, 0.35],
  foreR: [-1.0, 0, 0],
  handR: [0.3, 0, 0],
  armL: [-0.3, 0, -0.25],
  foreL: [-0.3, 0, 0],
  legR: [0.15, 0, 0.04],
  shinR: [0.2, 0, 0],
  legL: [-0.2, 0, -0.04],
  shinL: [0.2, 0, 0],
};
/** The hammer: down through its target, the trunk folding after it. */
const HAMMER: P = {
  root: [0, 0, 0, 0, -0.08, 0.1],
  body: [0.2, 0.12, 0],
  chest: [0.4, 0.3, -0.05],
  head: [0.05, -0.2, 0],
  jaw: [0.45, 0, 0],
  armR: [-0.7, 0, 0.1],
  foreR: [-0.15, 0, 0],
  handR: [-0.2, 0, 0],
  armL: [0.2, 0, -0.3],
  foreL: [-0.2, 0, 0],
  legR: [0.25, 0, 0.04],
  shinR: [0.25, 0, 0],
  legL: [-0.42, 0, -0.04],
  shinL: [0.4, 0, 0],
};

const attack = (): Clip<B> =>
  clip<B>('attack', 1.5, false, (b, t) => {
    timeline(b, t, [
      [0, RAISED],
      [0.05, { ...RAISED, armR: [-2.62, 0, 0.38], chest: [-0.24, -0.34, 0.05] }, outQuad],
      [0.13, HAMMER, inExpo],
      [0.45, { ...HAMMER, chest: [0.38, 0.28, -0.05], root: [0, 0, 0, 0, -0.075, 0.1] }, outCubic],
      [1.5, {}, inOut],
    ], { head: 0.04, handR: 0.03 });
    b.root.pos(0.014 * kick(t, 0.13, 7, 8), 0, 0);
  }, (t) => thud(t, [[0.13, 0.05]]));

/**
 * Slam: it rears up with both fists locked overhead (0.55 s), then brings them down into the ground before it
 * (0.75 s) with all its weight, bowed over them, and hauls itself back up.
 */
const slam = (): Clip<B> =>
  clip<B>('slam', 2.0, false, (b, t) => {
    const up: P = { root: [0, 0, 0, 0, 0.06, -0.06], body: [-0.12, 0, 0], chest: [-0.3, 0, 0], head: [-0.4, 0, 0], jaw: [0.5, 0, 0], armL: [-2.9, 0, 0.35], foreL: [-0.55, 0, 0], handL: [0.3, 0, 0], armR: [-2.9, 0, -0.35], foreR: [-0.55, 0, 0], handR: [0.3, 0, 0], legL: [-0.1, 0, -0.05], shinL: [0.1, 0, 0], legR: [-0.1, 0, 0.05], shinR: [0.1, 0, 0] };
    const down: P = { root: [0, 0, 0, 0, -0.2, 0.14], body: [0.4, 0, 0], chest: [0.55, 0, 0], head: [0.1, 0, 0], jaw: [0.3, 0, 0], armL: [-0.55, 0, 0.25], foreL: [-0.1, 0, 0], handL: [-0.25, 0, 0], armR: [-0.55, 0, -0.25], foreR: [-0.1, 0, 0], handR: [-0.25, 0, 0], legL: [-0.45, 0, -0.06], shinL: [0.7, 0, 0], legR: [-0.2, 0, 0.06], shinR: [0.65, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.45, up, inOut],
      [0.58, { ...up, chest: [-0.36, 0, 0], armL: [-3.0, 0, 0.35], armR: [-3.0, 0, -0.35] }, outQuad],
      [0.75, down, inExpo],
      [1.15, { ...down, root: [0, 0, 0, 0, -0.18, 0.14], chest: [0.5, 0, 0] }, outBack],
      [2.0, {}, inOut],
    ], { head: 0.05, handL: 0.03, handR: 0.03 });
    b.root.pos(0.02 * kick(t, 0.75, 7, 7), 0, 0);
    b.jaw.rot(0.3 * envelope(t, 0.7, 0.78, 0.95, 1.2));
  }, (t) => thud(t, [[0.75, 0.11]]), 'Golpe al suelo');

/**
 * Breath: it draws in (the chest swells, the head rears back), then lowers its head and opens its maw and the
 * cone of frost pours out of it (0.75–1.75 s), sweeping a little side to side, and it closes its jaws on the last.
 */
const breath = (): Clip<B> =>
  clip<B>('breath', 2.3, false, (b, t) => {
    const draw: P = { body: [-0.08, 0, 0], chest: [-0.28, 0, 0, 0, 0, 0, 1.05], head: [-0.45, 0, 0], jaw: [0.1, 0, 0], armL: [0.15, 0, -0.35], foreL: [-0.4, 0, 0], armR: [0.15, 0, 0.35], foreR: [-0.4, 0, 0], legL: [-0.15, 0, -0.05], shinL: [0.2, 0, 0], legR: [0.1, 0, 0.05], shinR: [0.15, 0, 0] };
    const blow: P = { root: [0, 0, 0, 0, -0.05, 0.05], body: [0.12, 0, 0], chest: [0.22, 0, 0], head: [0.1, 0, 0], jaw: [0.75, 0, 0], armL: [-0.35, 0, -0.45], foreL: [-0.5, 0, 0], armR: [-0.35, 0, 0.45], foreR: [-0.5, 0, 0], legL: [-0.32, 0, -0.05], shinL: [0.35, 0, 0], legR: [0.2, 0, 0.05], shinR: [0.25, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.55, draw, inOut],
      [0.75, blow, outCubic],
      [1.75, { ...blow, jaw: [0.7, 0, 0] }],
      [1.95, { ...blow, jaw: [0.05, 0, 0], head: [0.05, 0, 0] }, inQuad],
      [2.3, {}, inOut],
    ], { head: 0.04 });
    // The sweep across the cone, and the shudder of the blast.
    const on = envelope(t, 0.72, 0.85, 1.65, 1.8, outQuad);
    b.chest.rot(0, 0.18 * Math.sin((t - 0.75) * 3.2) * on, 0);
    b.head.rot(0.02 * Math.sin(t * 40) * on, 0.06 * Math.sin((t - 0.75) * 3.2) * on, 0);
    b.breath.scale(1 + (BLAST - 1) * on * (0.94 + 0.06 * Math.sin(t * 31)));
  }, (t) => thud(t, [[0.75, 0.02]]), 'Aliento helado');

const hit = (): Clip<B> =>
  clip<B>('hit', 0.5, false, (b, t) => {
    const q = settle(t, 2.0, 8) * span(t, 0, 0.04, outQuad);
    b.chest.rot(-0.08 * q, 0.05 * q);
    b.body.rot(-0.03 * q);
    b.head.rot(-0.18 * q, -0.08 * q, 0.05 * q);
    b.root.pos(0, 0, -0.03 * q);
    b.armL.rot(0.1 * q, 0, -0.05 * q);
    b.armR.rot(0.1 * q, 0, 0.05 * q);
    b.jaw.rot(0.2 * q);
  });

const stun = (): Clip<B> =>
  clip<B>('stun', 2.8, true, (b, t) => {
    const p = phase(t, 2.8) * TAU;
    b.root.pos(0, -0.06, 0).rot(0.02 * Math.sin(p), 0, 0.04 * Math.cos(p));
    b.body.rot(0.2, 0, 0);
    b.chest.rot(0.25 + 0.04 * Math.sin(p * 2), 0.08 * Math.sin(p), -0.05 * Math.cos(p));
    b.head.rot(0.4 + 0.12 * Math.sin(p + 0.8), 0.28 * Math.cos(p + 0.8), 0.2 * Math.sin(p + 0.8));
    b.jaw.rot(0.3);
    b.armL.rot(0.1 * Math.sin(p + 1.4), 0, 0.05);
    b.armR.rot(-0.1 * Math.sin(p + 1.4), 0, -0.05);
    b.legL.rot(-0.15);
    b.shinL.rot(0.3);
    b.legR.rot(-0.12);
    b.shinR.rot(0.28 + 0.06 * Math.sin(p));
  });

/** The fall (3 s): it rears with a soundless roar, its knees go (1.3 s), it sways, and topples onto its face (2.4 s). */
const die = (): Clip<B> =>
  clip<B>('die', 3.0, false, (b, t) => {
    const kneel: P = { legL: [0.0, 0, -0.1], shinL: [1.5, 0, 0], legR: [0.05, 0, 0.1], shinR: [1.5, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.35, { root: [0, 0, 0, 0, 0.02, -0.06], body: [-0.15, 0.1, 0], chest: [-0.3, 0.12, 0], head: [-0.6, 0.2, 0.1], jaw: [0.8, 0, 0], armL: [-1.6, 0, 0.6], foreL: [-0.4, 0, 0], armR: [-1.4, 0, -0.7], foreR: [-0.4, 0, 0] }, outExpo],
      [0.9, { root: [0, 0, 0.04, 0, -0.1, 0.04], body: [0.3, -0.1, 0.06], chest: [0.3, -0.1, 0], head: [0.4, 0, 0.2], jaw: [0.5, 0, 0], armL: [-0.3, 0, -0.1], foreL: [-0.2, 0, 0], armR: [-0.35, 0, 0.1], foreR: [-0.2, 0, 0], legL: [0.2, 0, -0.04], shinL: [0.4, 0, 0], legR: [-0.3, 0, 0.04], shinR: [0.45, 0, 0] }, inOut],
      [1.3, { ...kneel, root: [0, 0, 0, 0, -0.42, -0.04], body: [0.25, 0, 0.05], chest: [0.25, 0, 0], head: [0.5, 0, 0.25], jaw: [0.4, 0, 0], armL: [-0.1, 0, -0.1], armR: [-0.1, 0, 0.1] }, inQuad],
      [1.45, { ...kneel, root: [0, 0, 0, 0, -0.4, -0.04], body: [0.3, 0, 0.05], chest: [0.28, 0, 0], head: [0.6, 0, 0.28], jaw: [0.4, 0, 0], armL: [-0.08, 0, -0.1], armR: [-0.08, 0, 0.1] }, outBack],
      [1.95, { ...kneel, root: [0, 0, 0, 0, -0.42, -0.04], body: [0.45, 0.1, 0.1], chest: [0.35, 0.05, 0.05], head: [0.8, 0.2, 0.4], jaw: [0.5, 0, 0], armL: [0.1, 0, -0.05], armR: [0.12, 0, 0.08] }, inOut],
      [2.4, { ...kneel, root: [0, 0, 0, 0, -0.5, 0.06], body: [1.3, 0.1, 0.12], chest: [0.3, 0.05, 0.05], head: [0.2, 0.5, 0.6], jaw: [0.5, 0, 0], armL: [-2.2, 0, -0.5], foreL: [-0.05, 0, 0], armR: [-2.1, 0, 0.55], foreR: [-0.05, 0, 0] }, inCubic],
      [3.0, { ...kneel, root: [0, 0, 0, 0, -0.5, 0.06], body: [1.26, 0.1, 0.12], chest: [0.28, 0.05, 0.05], head: [0.3, 0.6, 0.7], jaw: [0.45, 0, 0], armL: [-2.25, 0, -0.52], foreL: [0.02, 0, 0], armR: [-2.15, 0, 0.57], foreR: [0.02, 0, 0] }, outBack],
    ], { head: 0.06, foreL: 0.04, foreR: 0.04 });
    b.root.pos(0.015 * kick(t, 1.3, 7, 8) + 0.02 * kick(t, 2.4, 7, 8), 0, 0);
  }, (t) => thud(t, [[1.3, 0.05], [2.4, 0.08]]));

/** Clips by library kind (see ../rigs.ts). */
export const frostColossusClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die(), breath(), slam()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { frostColossus: frostColossusClips };

